import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import type { VaultMediaItemDto } from '@api'
import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { CheckedState } from '@/components/ui/form/form-checkbox'
import { TextField } from '@/components/ui/text-field.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { AI_LABEL_PREFIX } from '@/consts/vault.ts'
import type { DialogProps } from '@/dialogs/types'
import { $isAiChattingEnabled } from '@/models/ai-features/ai-chatting.ts'
import { updateMediaSettingsFx } from '@/models/vault/medias'
import { setSelectedMedia } from '@/models/vault/medias/selection.ts'

const removeAiLabelPrefix = (value: string) =>
  value.replaceAll(AI_LABEL_PREFIX, '')

const ensureAiLabelPrefix = (value: string) =>
  `${AI_LABEL_PREFIX} ${removeAiLabelPrefix(value).trimStart()}`

interface Props extends DialogProps {
  item: VaultMediaItemDto
}

export const MediaSettingsDialog = ({ isOpen, item, onClose }: Props) => {
  // const isScriptsLoading = useUnit(getScriptsFx.pending)
  // const scripts = useUnit($scripts)
  const isMediaSettingsLoading = useUnit(updateMediaSettingsFx.pending)
  const isAiChattingFeatureEnabled = useUnit($isAiChattingEnabled)
  const inputRef = useRef<HTMLInputElement>(null)

  const [label, setLabel] = useState(item.mediaAttributes?.label || '')
  const [price, setPrice] = useState(item.mediaAttributes?.price || 0)
  const scriptId = item.mediaAttributes?.scriptId ?? 0
  const aiCheckboxId = useId()
  const isAiChattingEnabled = label.startsWith(AI_LABEL_PREFIX)

  const handleSave = useCallback(async () => {
    const scriptIdNumber = Number(scriptId)

    let normalizedLabel = label

    if (isAiChattingFeatureEnabled) {
      normalizedLabel = isAiChattingEnabled
        ? ensureAiLabelPrefix(label)
        : removeAiLabelPrefix(label)
    }

    setLabel(normalizedLabel)
    await updateMediaSettingsFx({
      mediaId: item.id,
      label: normalizedLabel,
      price: price ? price : undefined,
      scriptId: !scriptIdNumber ? undefined : scriptIdNumber,
    })
    onClose()
  }, [
    isAiChattingEnabled,
    isAiChattingFeatureEnabled,
    item,
    label,
    onClose,
    price,
    scriptId,
  ])

  const handlePriceChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = +event.target.value

      setPrice(isNaN(newValue) ? 0 : newValue)
    },
    [],
  )

  const handleAiCheckboxChange = useCallback((checked: CheckedState) => {
    const shouldEnable = checked === true

    setLabel((currentLabel) =>
      shouldEnable
        ? ensureAiLabelPrefix(currentLabel)
        : removeAiLabelPrefix(currentLabel),
    )
  }, [])

  useEffect(() => {
    setSelectedMedia(item)
  }, [item])

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      initialFocus={inputRef}
    >
      <Dialog.Header>
        <Dialog.Title>Media settings</Dialog.Title>
      </Dialog.Header>

      <div className="mt-5">
        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between">
            <label htmlFor="media-label-input">Media Label</label>
            {isAiChattingFeatureEnabled && (
              <Tooltip
                asChild
                content={
                  <span>
                    <strong>Important:</strong> Special symbol will be added to
                    the label. Don&apos;t delete it manually. <br />
                    Add detailed description for AI to use media correctly.
                  </span>
                }
              >
                <label
                  htmlFor={aiCheckboxId}
                  className="flex items-center gap-2 text-sm text-title cursor-pointer"
                >
                  <Checkbox
                    id={aiCheckboxId}
                    checked={isAiChattingEnabled}
                    onCheckedChange={handleAiCheckboxChange}
                  />
                  Enable media for AI Chatting
                </label>
              </Tooltip>
            )}
          </div>
          <TextField
            ref={inputRef}
            id="media-label-input"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            className="mt-0.5"
          />
        </div>

        <div className="mb-3">
          <label>Price</label>
          <TextField
            value={price}
            onChange={handlePriceChange}
            prefix={<span className="text-secondary-300">$</span>}
            className="mt-0.5"
          />
        </div>

        {/* Removed for now <div className="mb-3">
          <label className="mb-0.5 block">Highlight script</label>
          <Select
            value={scriptId ? scriptId.toString() : undefined}
            onValueChange={setScriptId}
            disabled={isScriptsLoading}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[40vh] w-[300px]">
              <SelectGroup>
                <SelectItem value="0">No script</SelectItem>
                {scripts?.map(({ name, id }) => (
                  <SelectItem key={id} value={id.toString()}>
                    {name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>*/}
      </div>

      <Dialog.Footer className="grid grid-cols-2 gap-2">
        <Button
          disabled={isMediaSettingsLoading}
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          isLoading={isMediaSettingsLoading}
          onClick={handleSave}
        >
          Save
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
